/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * PreferenceSettingModal
 *
 * @file PreferenceSettingModal.jsx
 * @author Anuja Gaikwad
 * @since 1.0.27
  */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@material-ui/core';
import DynamicToggle from '../common/DynamicToggle';
import usePreferenceSettingStyles from './styles/PreferenceSettingStyles';
import Icons from '../icons/Icon';

const PreferenceSettingModal = (props) => {
  const {
    open,
    onClose,
    onConfirm,
    preferenceSettings,
    onToggleChange
  } = props;

  const classes = usePreferenceSettingStyles(props);

  const [toggles, setToggles] = useState({});

  useEffect(() => {
    if (!preferenceSettings?.sections) return;

    const initialToggles = preferenceSettings.sections.reduce((acc, { key, value }) => {
      acc[key] = value;

      return acc;
    }, {});

    setToggles(initialToggles);
  }, [preferenceSettings]);

  const handleToggleChange = (key) => {
    setToggles(prev => ({ ...prev, [key]: !prev[key] }));
    onToggleChange(key, !toggles[key]);
  };

  const getInitialValue = (key) => {
    const section = preferenceSettings?.sections?.find(item => item.key === key);

    return section?.value;
  };

  const hasChanged = () => Object.keys(toggles).some(key => toggles[key] !== getInitialValue(key));

  const handleConfirm = () => {
    // send only the changed toggles
    const changedToggles = Object.keys(toggles).reduce((acc, key) => {
      if (toggles[key] !== getInitialValue(key)) {
        acc[key] = toggles[key];
      }

      return acc;
    }, {});

    onConfirm(changedToggles);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        classes={{ root: classes.closeButton }}
      >
        <Icons name="CloseIcon" />
      </IconButton>
      <DialogTitle classes={{ root: classes.titleRoot }}>
        <div className={classes.title}>{preferenceSettings.title}</div>
        <div className={classes.subtitle}>{preferenceSettings.subTitle}</div>
      </DialogTitle>
      <DialogContent classes={{ root: classes.contentRoot }}>
        <div className={classes.sectionsWrapper}>
          {preferenceSettings.sections.map((item) => (
            <div key={item.key} className={classes.sectionCard}>
              {/* Header row */}
              <div className={classes.mainRow}>
                <div className={classes.label}>{item.label}</div>
                <DynamicToggle
                  label=""
                  value={toggles[item?.key] ?? false}
                  onChange={() => handleToggleChange(item.key)}
                />
              </div>

              {/* SubLabel */}
              {item.subLabel && (
                <div className={classes.subLabel}>
                  <span>{item.subLabel}</span>
                </div>
              )}

              {/* Divider */}
              {item.showDivider && <div className={classes.studyPrepDivider} />}

              {/* DescriptionText */}
              {item.descriptionText?.length > 0 && (
              <div className={classes.subLabel}>
                {item.descriptionText?.map((desc, idx) => (
                  <div
                    key={idx}
                    dangerouslySetInnerHTML={{ __html: desc }}
                  />
                ))}
              </div>
              )}
              {/* Help / Learn more */}
              {item.helpLinkText && item.onHelpLinkClick && (
                <a
                  href="#"
                  className={classes.learnMore}
                  onClick={(e) => {
                    e.preventDefault();
                    item.onHelpLinkClick(); // call parent callback
                  }}
                >
                  {item.helpLinkText}
                </a>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
      <DialogActions classes={{ root: classes.actionsRoot }}>
        <div className={classes.footer}>
          <button
            type="button"
            className={classes.confirmButton}
            onClick={handleConfirm}
            disabled={!hasChanged()}
          >
            {preferenceSettings.ctaLabel}
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

PreferenceSettingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onToggleChange: PropTypes.func,
  preferenceSettings: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    ctaLabel: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        subLabel: PropTypes.string,
        value: PropTypes.bool.isRequired,
        descriptionText: PropTypes.arrayOf(PropTypes.string),
        showDivider: PropTypes.bool,
        helpLinkText: PropTypes.string
      })
    ).isRequired
  }).isRequired
};
PreferenceSettingModal.defaultProps = {
  open: false,
  onClose: () => {},
  onConfirm: null,
  onToggleChange: () => {}
};
export default PreferenceSettingModal;
